/**
 * 메일 서식
 * ---------------------------------------------------------------------------
 * 왜 이 화면이 있는가
 *   보내는 글은 상황에 따라 자주 바뀐다. 문구 하나 고치려고 배포를 기다려야 하면
 *   결국 아무도 고치지 않고, 어색한 문장이 고객에게 계속 나간다.
 *
 *   특히 「기업 파기 예고」 처럼 되돌릴 수 없는 일을 알리는 글은, 받는 사람이
 *   무엇을 해야 하는지 바로 알 수 있어야 한다. 그래서 글을 DB 에 두고 여기서
 *   고친다. 저장하면 다음 발송부터 바로 쓰인다.
 *
 * 변수
 *   본문의 `{{이름}}` 은 보낼 때 실제 값으로 바뀐다. 서식마다 쓸 수 있는 이름이
 *   정해져 있어 목록에 보여 준다 — 없는 이름을 적으면 그 자리는 빈칸이 된다.
 *   막지는 않는다. 막으려고 검사를 빡빡하게 하면 안내문 속 예시 중괄호까지
 *   거부된다.
 *
 * 코드·채널은 못 바꾼다
 *   코드는 보내는 쪽 프로그램이 가리키는 이름이다. 바꾸면 그 메일이 조용히
 *   기본 문구로 나간다(서식을 못 찾으면 코드 이름이 그대로 제목이 된다).
 *
 * 언제 나가는 글인가
 *   글만 보여 주면 **저절로 나가는 글인지 사람이 눌러야 나가는 글인지** 알 수
 *   없다. 「이 메일이 왜 안 왔지」와 「이거 고치면 누구한테 나가지」를 물어볼
 *   곳이 코드밖에 없었다. 그래서 계기·받는 사람·다음 발송 시각을 함께 보여
 *   준다.
 *
 *   시각은 **돌고 있는 스케줄러에서 읽어 온 값**이다. 화면에 베껴 적은 문장이
 *   아니므로, 배치 시각을 고치면 여기도 따라 바뀌고 등록이 빠졌으면
 *   「등록 안 됨」으로 드러난다.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Input,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

/** 돌고 있는 배치에서 읽어 온 실제 일정 */
interface ScheduleFact {
  job: string;
  /** 실제 등록된 크론 식 */
  cron: string | null;
  nextRunAt: string | null;
  /** 그 이름으로 등록돼 있는가. 거짓이면 그 배치는 돌지 않는다 */
  registered: boolean;
}

interface Trigger {
  label: string;
  kind: 'SCHEDULE' | 'EVENT' | 'MANUAL' | 'NOT_MAIL';
  auto: boolean;
  when: string;
  audience: string;
  source: string;
  caveat?: string;
  schedule: ScheduleFact | null;
}

interface MessageTemplate {
  templateCode: string;
  channel: string;
  locale: string;
  subject: string | null;
  body: string;
  bodyFormat: string;
  variables: string[];
  isActive: boolean;
  updatedAt: string | null;
  /** 서버가 알려 주는 계기. 목록에 없는 코드면 null */
  trigger: Trigger | null;
}

const CHANNEL_LABELS: Record<string, string> = {
  EMAIL: '메일',
  SMS: '문자',
  LMS: '장문 문자',
  ALIMTALK: '알림톡',
};

/**
 * 무엇이 이 글을 내보내는가.
 *
 * 「자동」과 「사람이 할 때」를 가르는 것이 핵심이다. 자동인 글은 고치면
 * **다음 배치 때 전 기업에** 나가고, 그렇지 않은 글은 그 동작을 한 사람에게만
 * 나간다. 고치기 전에 그 차이를 알아야 한다.
 */
const KIND_META: Record<Trigger['kind'], { text: string; color: string }> = {
  SCHEDULE: { text: '자동', color: 'blue' },
  EVENT: { text: '사람 동작', color: 'green' },
  MANUAL: { text: '운영자가 누를 때', color: 'gold' },
  NOT_MAIL: { text: '메일 아님', color: 'default' },
};

/** 「2026. 11. 3. 오전 2:00」. 값이 없으면 「—」가 아니라 사유를 적는다. */
function nextRunText(s: ScheduleFact | null): string {
  if (!s) return '—';
  if (!s.registered) return '등록 안 됨';
  if (!s.nextRunAt) return '멈춰 있음';
  return new Date(s.nextRunAt).toLocaleString('ko-KR');
}

export function MessageTemplatePage() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<MessageTemplate | null>(null);

  const templates = useQuery<MessageTemplate[], NormalizedError>({
    queryKey: ['admin', 'message-templates'],
    queryFn: async () =>
      (await apiClient.get<MessageTemplate[]>('/admin/message-templates')).data,
  });

  const save = useMutation({
    mutationFn: async (values: { subject: string; body: string; isActive: boolean }) => {
      if (!editing) return;
      const { templateCode, channel, locale } = editing;
      await apiClient.put(
        `/admin/message-templates/${encodeURIComponent(templateCode)}/${encodeURIComponent(
          channel,
        )}/${encodeURIComponent(locale)}`,
        {
          subject: values.subject?.trim() ? values.subject.trim() : null,
          body: values.body,
          isActive: values.isActive,
        },
      );
    },
    onSuccess: async () => {
      message.success('저장했습니다. 다음 발송부터 이 내용이 나갑니다.');
      setEditing(null);
      await client.invalidateQueries({ queryKey: ['admin', 'message-templates'] });
    },
    onError: (err: NormalizedError) => message.error(errorText(err)),
  });

  const openEdit = (row: MessageTemplate) => {
    setEditing(row);
    form.setFieldsValue({
      subject: row.subject ?? '',
      body: row.body,
      isActive: row.isActive,
    });
  };

  const submit = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    save.mutate(values);
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        loading={templates.isLoading}
        title={
          <Space>
            <FileTextOutlined />
            <span>메일 서식</span>
          </Space>
        }
      >
        <Paragraph type="secondary">
          플랫폼이 보내는 글의 제목과 본문입니다. 저장하면 <strong>다음 발송부터 바로</strong>{' '}
          적용됩니다. 본문의 <Text code>{'{{이름}}'}</Text> 은 보낼 때 실제 값으로 바뀝니다.
        </Paragraph>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="「발송」 칸을 먼저 보세요"
          description={
            <>
              <Tag color="blue">자동</Tag> 은 <strong>시스템이 때가 되면 저절로</strong> 보내는
              글입니다. 고치면 다음 배치 때 해당하는 모든 기업에 그대로 나갑니다.{' '}
              <Tag color="green">사람 동작</Tag> 은 가입·초대처럼 누군가 그 동작을 했을 때만,{' '}
              <Tag color="gold">운영자가 누를 때</Tag> 는 운영자가 단추를 눌렀을 때만 나갑니다.{' '}
              <Tag>메일 아님</Tag> 은 메일로 나가지 않고 화면에 띄우는 글입니다.
              <br />
              <strong>다음 발송</strong> 시각은 지금 돌고 있는 배치에서 읽어 온 값입니다 —
              화면에 적어 둔 문장이 아니므로 실제와 어긋나지 않습니다.
            </>
          }
        />

        <Table<MessageTemplate>
          rowKey={(r) => `${r.templateCode}|${r.channel}|${r.locale}`}
          dataSource={templates.data ?? []}
          size="small"
          pagination={false}
          columns={[
            {
              title: '서식',
              dataIndex: 'templateCode',
              render: (code: string, row: MessageTemplate) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{row.trigger?.label ?? code}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {code}
                  </Text>
                </Space>
              ),
            },
            {
              title: '경로',
              dataIndex: 'channel',
              width: 90,
              render: (ch: string) => CHANNEL_LABELS[ch] ?? ch,
            },
            {
              title: '발송',
              width: 320,
              render: (_: unknown, row: MessageTemplate) => {
                /*
                 * 계기를 모르는 코드는 빈칸으로 두지 않는다. 새 서식이 생겼는데
                 * 목록에 안 적힌 것이므로, **빠뜨렸다는 사실**이 보여야 한다.
                 */
                if (!row.trigger) {
                  return (
                    <Space direction="vertical" size={0}>
                      <Tag color="red">확인되지 않음</Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        언제 나가는 글인지 등록돼 있지 않습니다
                      </Text>
                    </Space>
                  );
                }
                const meta = KIND_META[row.trigger.kind];
                return (
                  <Space direction="vertical" size={2}>
                    <Space size={4}>
                      <Tag color={meta.color}>{meta.text}</Tag>
                      {row.trigger.schedule && !row.trigger.schedule.registered && (
                        <Tag color="red">배치 등록 안 됨</Tag>
                      )}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {row.trigger.when}
                    </Text>
                  </Space>
                );
              },
            },
            {
              title: '다음 발송',
              width: 170,
              render: (_: unknown, row: MessageTemplate) =>
                row.trigger?.kind === 'SCHEDULE' ? (
                  <Text style={{ fontSize: 12 }}>{nextRunText(row.trigger.schedule)}</Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    정해진 때 없음
                  </Text>
                ),
            },
            {
              title: '사용',
              dataIndex: 'isActive',
              width: 90,
              render: (on: boolean) =>
                on ? <Tag color="green">사용</Tag> : <Tag>사용 안 함</Tag>,
            },
            {
              title: '마지막 수정',
              dataIndex: 'updatedAt',
              width: 160,
              render: (at: string | null) =>
                at ? new Date(at).toLocaleString('ko-KR') : '—',
            },
            {
              title: '',
              width: 80,
              render: (_: unknown, row: MessageTemplate) => (
                <Button size="small" onClick={() => openEdit(row)}>
                  수정
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Drawer
        open={!!editing}
        width={720}
        onClose={() => setEditing(null)}
        title={
          editing ? `${editing.trigger?.label ?? editing.templateCode} — 서식 수정` : ''
        }
        extra={
          <Space>
            <Button onClick={() => setEditing(null)}>취소</Button>
            <Button type="primary" loading={save.isPending} onClick={() => void submit()}>
              저장
            </Button>
          </Space>
        }
      >
        {editing && (
          <Form form={form} layout="vertical" requiredMark={false}>
            {/*
              고치기 **전에** 누구에게 언제 나가는 글인지 보여 준다. 저장하면
              다음 발송부터 바로 적용되므로, 자동으로 나가는 글은 고친 그날
              밤에 전 기업에 나간다.
            */}
            {editing.trigger && (
              <Descriptions
                size="small"
                column={1}
                bordered
                style={{ marginBottom: 16 }}
                items={[
                  {
                    key: 'kind',
                    label: '발송',
                    children: (
                      <Space size={4} wrap>
                        <Tag color={KIND_META[editing.trigger.kind].color}>
                          {KIND_META[editing.trigger.kind].text}
                        </Tag>
                        <span>{editing.trigger.when}</span>
                      </Space>
                    ),
                  },
                  {
                    key: 'audience',
                    label: '받는 사람',
                    children: editing.trigger.audience,
                  },
                  ...(editing.trigger.kind === 'SCHEDULE'
                    ? [
                        {
                          key: 'schedule',
                          label: '다음 발송',
                          children: (
                            <Space direction="vertical" size={0}>
                              <span>{nextRunText(editing.trigger.schedule)}</span>
                              {editing.trigger.schedule?.cron && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  배치 <Text code>{editing.trigger.schedule.job}</Text>{' '}
                                  <Text code>{editing.trigger.schedule.cron}</Text> (서울 기준)
                                </Text>
                              )}
                              {editing.trigger.schedule &&
                                !editing.trigger.schedule.registered && (
                                  <Text type="danger" style={{ fontSize: 12 }}>
                                    이 배치가 서버에 등록돼 있지 않습니다. 지금은 이 글이
                                    저절로 나가지 않습니다.
                                  </Text>
                                )}
                            </Space>
                          ),
                        },
                      ]
                    : []),
                  ...(editing.trigger.caveat
                    ? [
                        {
                          key: 'caveat',
                          label: '알아 둘 것',
                          children: <Text type="warning">{editing.trigger.caveat}</Text>,
                        },
                      ]
                    : []),
                ]}
              />
            )}

            {editing.trigger?.kind === 'NOT_MAIL' && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="이 글은 메일로 나가지 않습니다"
                description="화면에 띄우는 안내문입니다. 고쳐도 메일함에는 아무것도 가지 않습니다."
              />
            )}

            {editing.variables.length > 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="이 서식에서 쓸 수 있는 값"
                description={
                  <Space wrap size={[4, 4]}>
                    {editing.variables.map((v) => (
                      <Text code key={v}>{`{{${v}}}`}</Text>
                    ))}
                  </Space>
                }
              />
            )}

            <Form.Item
              name="subject"
              label="제목"
              extra="메일에만 쓰입니다. 문자·알림톡은 제목이 없습니다."
            >
              <Input maxLength={300} placeholder="[WORKnHub] …" />
            </Form.Item>

            <Form.Item
              name="body"
              label="본문"
              rules={[{ required: true, message: '본문을 입력하세요' }]}
              extra={
                editing.bodyFormat === 'HTML'
                  ? 'HTML 로 나갑니다. 태그를 그대로 적으세요.'
                  : '글자 그대로 나갑니다. 줄바꿈도 그대로 보입니다.'
              }
            >
              <Input.TextArea rows={18} style={{ fontFamily: 'monospace', fontSize: 13 }} />
            </Form.Item>

            <Form.Item
              name="isActive"
              label="사용"
              valuePropName="checked"
              extra="끄면 이 서식으로 나가던 메일이 기본 문구로 나갑니다. 끄지 말고 내용을 고치세요."
            >
              <Switch />
            </Form.Item>
          </Form>
        )}
      </Drawer>
    </Space>
  );
}
